import type { FormikErrors } from 'formik'
import { formatCredits } from '@/lib/billing/utils/credits'
import { formatCentsAsUsd } from '@/lib/billing/utils/format'
import { MICRO_USD_PER_CREDIT } from '@/lib/billing/constants'
import { NUMERIC_FIELDS as F, type NumericField } from './catalog'
import { hoursWithDays } from './change-format'
import { parseAmount, validateEditor, type PlanEditorValues } from './model'

export interface SummaryRow {
  label: string
  value: string
}

export function buildCompactSummary(values: PlanEditorValues): SummaryRow[] {
  const errors = validateEditor(values)
  const value = (field: NumericField, zero?: string) => summaryValue(values, errors, field, zero)
  return [
    { label: 'Monthly price', value: value(F.price, 'Free') },
    { label: 'Starting credits', value: value(F.initial, 'No grant') },
    { label: 'Monthly credits', value: value(F.recurring, 'No grant') },
    { label: 'Voice', value: voiceAccess(values, value) },
    { label: 'Campaigns', value: values.campaigns_enabled ? `Enabled · ${value(F.campaigns, '0')} campaigns` : 'Disabled' },
    { label: 'Shopify Insights', value: values.shopify_insights_trial_duration_hours === null ? 'Included' : `Trial · ${value(F.insights)}` },
    { label: 'Users', value: value(F.users, 'None') },
    { label: 'Knowledge storage', value: value(F.size, 'None') },
    { label: 'Voice agents', value: voiceAgents(values, errors, value) },
    { label: 'Connected stores', value: value(F.stores, 'None') },
    { label: 'CRM connections', value: value(F.crm, 'None') },
  ]
}

function summaryValue(values: PlanEditorValues, errors: FormikErrors<PlanEditorValues>, field: NumericField, zero?: string): string {
  if (errors[field.key]) return 'Needs attention'
  const raw = values[field.key]
  if (raw === null) return field.empty ?? 'Not set'
  const amount = parseAmount(raw, field)
  if (amount === null) return 'Needs attention'
  if (amount === 0 && (zero || field.zero)) return zero || field.zero!
  if (field.scale === MICRO_USD_PER_CREDIT) return formatCredits(amount)
  if (field.scale === 100) return formatCentsAsUsd(amount)
  if (field.unit === 'hours') return hoursWithDays(amount)
  return field.unit ? `${amount} ${field.unit}` : String(amount)
}

function voiceAccess(values: PlanEditorValues, value: (field: NumericField) => string): string {
  if (values.voice_included) return 'Included'
  if (values.voice_trial_enabled) return `Trial · ${value(F.voiceDuration)}`
  return 'Not offered'
}

// With voice access or a trial, a zero agent limit still permits the first agent.
function voiceAgents(values: PlanEditorValues, errors: FormikErrors<PlanEditorValues>, value: (field: NumericField) => string): string {
  const offered = values.voice_included || values.voice_trial_enabled
  if (offered && !errors.voice_agents_limit && parseAmount(values.voice_agents_limit, F.agents) === 0) return '1 agent to start'
  return value(F.agents)
}
